import * as React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { font, get, media } from 'styles/utils';

export const variants = {
  hero: css`
    font-family: ${font('heading')};
    font-size: 2.625rem;
    font-weight: bold;
    line-height: 1.2;
    text-wrap: balance;

    /* Prevent clipping of descenders */
    padding-bottom: 0.125em;

    ${media.min('md')} {
      font-size: 3.25rem;
    }

    @supports (-webkit-background-clip: text) {
      background-image: linear-gradient(to top, #fff4, #fff 0.85em);
      -webkit-background-clip: text;
      color: transparent;
    }

    @supports (background-clip: text) {
      background-image: linear-gradient(to top, #fff4, #fff 0.85em);
      background-clip: text;
      color: transparent;
    }
  `,
  f1: css`
    font-size: 50px;
    line-height: 60px;
    font-family: ${font('heading')};
    font-weight: bold;
    ${media.min('md')} {
      font-size: 60px;
      line-height: 70px;
    }
  `,
  f2: css`
    font-size: 38px;
    line-height: 46px;
    font-family: ${font('heading')};
    font-weight: normal;
    ${media.min('md')} {
      font-size: 46px;
      line-height: 56px;
    }
  `,
  f3: css`
    font-size: 30px;
    line-height: 38px;
    font-family: ${font('heading')};
    font-weight: normal;
    ${media.min('md')} {
      font-size: 38px;
      line-height: 48px;
    }
  `,
  f4: css`
    font-size: 26px;
    line-height: 32px;
    font-family: ${font('heading')};
    font-weight: bold;
    ${media.min('md')} {
      font-size: 30px;
      line-height: 40px;
    }
  `,
  f5: css`
    font-size: 20px;
    line-height: 26px;
    font-family: ${font('heading')};
    font-weight: bold;
    ${media.min('md')} {
      font-size: 26px;
      line-height: 34px;
    }
  `,
  f6: css`
    font-family: ${font('heading')};
    font-size: 14px;
    font-weight: bold;
    letter-spacing: 0.09em;
    line-height: 19px;
    text-transform: uppercase;
  `,
  p: css`
    font-size: 18px;
    line-height: 28px;
    font-weight: normal;
    ${media.min('md')} {
      font-size: 20px;
      line-height: 30px;
    }
  `,
  base: css`
    font-size: 1.125rem;
    line-height: 1.5;
    ${media.min('md')} {
      font-size: 1.25;
    }
  `,
  smallP: css`
    font-size: 16px;
    line-height: 26px;
    font-weight: normal;
  `,
  smallestP: css`
    font-size: 14px;
    line-height: 20px;
    font-weight: normal;
  `,
  xs: css`
    font-size: 0.75rem;
    line-height: 1.625;
  `,
  sm: css`
    font-size: 0.875rem;
    line-height: 1.625;
  `,
  md: css`
    font-size: 1rem;
    line-height: 1.625;
  `,
  lg: css`
    font-size: 1.375rem;
    line-height: 1.5;
  `,
  xl: css`
    font-size: 1.5rem;
    font-weight: bold;
    line-height: 1.375;
  `,
  'f1.resources': css`
    font-size: 50px;
    line-height: 60px;
    font-family: ${font('heading')};
    font-weight: bold;
    ${media.min('md')} {
      font-size: 64px;
      line-height: 83px;
    }
  `,
  'f2.resources': css`
    font-size: 38px;
    line-height: 46px;
    font-family: ${font('heading')};
    font-weight: normal;
    ${media.min('md')} {
      font-size: 36px;
      line-height: 50px;
    }
  `,
  'f3.resources': css`
    font-size: 30px;
    line-height: 38px;
    font-family: ${font('heading')};
    font-weight: normal;
    ${media.min('md')} {
      font-size: 20px;
      line-height: 32px;
    }
  `,
  'p.resources': css`
    font-size: 18px;
    line-height: 28px;
    font-weight: normal;
    ${media.min('md')} {
      font-size: 16px;
      line-height: 27px;
    }
  `,
  eyebrow: css`
    font-family: ${font('heading')};
    font-size: 0.875rem;
    font-weight: bold;
    letter-spacing: 0.09em;
    line-height: 1.375;
    text-transform: uppercase;
  `,
  'eyebrow.lg': css`
    font-family: ${font('heading')};
    font-size: 1.3125rem;
    font-weight: bold;
    letter-spacing: 0.09em;
    line-height: 1.375;
    text-transform: uppercase;
  `,
  'heading.section': css`
    -webkit-font-smoothing: antialiased;
    font-family: ${font('heading')};
    font-size: 2rem;
    font-weight: bold;
    line-height: 1.2;
    text-wrap: pretty;

    ${media.min('md')} {
      font-size: 2.5rem;
    }
  `,
  'heading.subsection': css`
    -webkit-font-smoothing: antialiased;
    font-family: ${font('heading')};
    font-size: 1.5rem;
    font-weight: bold;
    line-height: 1.2;
    text-wrap: pretty;

    ${media.min('md')} {
      font-size: 2.25rem;
    }
  `,
  'heading.feature': css`
    font-family: ${font('heading')};
    font-size: 1.5rem;
    font-weight: normal;
    line-height: 1.3;

    ${media.min('md')} {
      font-size: 1.625rem;
    }
  `,
  'heading.feature.sm': css`
    font-family: ${font('heading')};
    font-size: 1.375rem;
    font-weight: normal;
    line-height: 1.4;
  `,
  'heading.feature.lg': css`
    font-family: ${font('heading')};
    font-size: 1.625rem;
    font-weight: bold;
    line-height: 1.2;
    text-wrap: balance;
  `,
};

const StyledText = styled.p`
  ${({ $align }) => $align && `text-align: ${$align};`}

  color: ${({ $color, theme }) => get(theme.colors, $color, $color)};

  ${({ $variant }) => $variant && variants[$variant]}

  ${({ $wrap }) => $wrap && `text-wrap: ${$wrap};`}
`;

function Text({ align, color, variant, wrap, ...rest }) {
  return (
    <StyledText
      $align={align}
      $color={color}
      $variant={variant}
      $wrap={wrap}
      {...rest}
    />
  );
}

Text.propTypes = {
  align: PropTypes.oneOf(['left', 'right', 'center']),
  color: PropTypes.oneOf([
    'bgBlueGray',
    'lightestBlue',
    'skyBlue',
    'lightestTeal',
    'lightTeal',
    'teal',
    'mediumTeal',
    'darkTeal',
    'darkestTeal',
    'textGray',
    'textTeal',
    'yellow',
    'red',
    'white',
  ]),
  variant: PropTypes.oneOf(Object.keys(variants)),
  wrap: PropTypes.oneOf(['pretty', 'balance', 'wrap', 'nowrap']),
};

Text.defaultProps = {
  variant: 'p',
};

export default Text;
